/**
 * DMSDocumentService - Document Management System integration for ClientFirst
 * 
 * Handles document persistence, versioning, and synchronization with the DMS backend
 * while maintaining ClientFirst's text-based architecture through AST conversion.
 */

import axios, { AxiosInstance } from 'axios';
import { TextToASTConverter, ConversionOptions, BackendAST } from './TextToASTConverter';
import { DocumentLogger } from '@/utils/loggers';

export interface DocumentCreateData {
  title: string;
  content: string; // ClientFirst text
  client_id?: string;
  matter_id?: string;
  document_type?: string;
}

export interface DocumentVersionData {
  content: string; // ClientFirst text
  change_summary?: string;
}

export interface DocumentResponse {
  document_id: string;
  version_id: string;
  version_number: number;
}

export interface DocumentLoadResult {
  document: any;
  textContent: string; // Converted from AST for ClientFirst
  latestVersion: any;
  workingCopy?: string;
  pageSetupMetadata?: any; // Page setup metadata from database
}

export interface SaveResult {
  success: boolean;
  message: string;
  versionId?: string;
  timestamp?: Date;
  errors?: string[];
}

export class DMSDocumentService {
  private http: AxiosInstance;
  private astConverter: TextToASTConverter;
  private baseUrl: string;

  constructor(baseUrl: string, token: string) {
    this.baseUrl = baseUrl;
    this.astConverter = new TextToASTConverter();
    
    this.http = axios.create({
      baseURL: baseUrl,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Create new document with first version
   */
  async createDocument(documentData: DocumentCreateData): Promise<DocumentResponse> {
    const startTime = performance.now();
    DocumentLogger.createStarted(documentData.title, documentData.document_type);
    
    try {
      console.log('[DMSDocumentService] Creating document:', documentData.title);

      // Convert ClientFirst text to AST for backend
      const conversionOptions: ConversionOptions = {
        documentType: documentData.document_type,
        preserveFormatting: true
      };

      const astResult = this.astConverter.convertTextToAST(documentData.content, conversionOptions);
      
      if (!astResult.success || !astResult.ast) {
        throw new Error(`AST conversion failed: ${astResult.errors?.join(', ')}`);
      }

      // Log conversion warnings if any
      if (astResult.warnings && astResult.warnings.length > 0) {
        console.warn('[DMSDocumentService] AST conversion warnings:', astResult.warnings);
      }

      // Prepare payload with both text and AST
      const payload = {
        ...documentData,
        ast: astResult.ast
      };

      const response = await this.http.post('/api/v1/dms/documents', payload);
      
      console.log('[DMSDocumentService] Document created successfully:', response.data);
      
      const createTimeMs = performance.now() - startTime;
      DocumentLogger.createCompleted(response.data.document_id, {
        title: documentData.title,
        versionId: response.data.version_id,
        createTimeMs,
      });
      
      return {
        document_id: response.data.document_id,
        version_id: response.data.version_id,
        version_number: response.data.version_number || 1
      };

    } catch (error) {
      console.error('[DMSDocumentService] Document creation failed:', error);
      DocumentLogger.createFailed(documentData.title, this.extractErrorMessage(error));
      throw new Error(`Failed to create document: ${this.extractErrorMessage(error)}`);
    }
  }

  /**
   * Create new version of existing document
   */
  async createNewVersion(documentId: string, versionData: DocumentVersionData): Promise<DocumentResponse> {
    try {
      console.log('[DMSDocumentService] Creating new version for document:', documentId);

      // Convert ClientFirst text to AST for backend
      const astResult = this.astConverter.convertTextToAST(versionData.content);
      
      if (!astResult.success || !astResult.ast) {
        throw new Error(`AST conversion failed: ${astResult.errors?.join(', ')}`);
      }

      // Prepare payload with both text and AST
      const payload = {
        content: versionData.content,
        ast: astResult.ast,
        change_summary: versionData.change_summary || 'Document updated'
      };

      const response = await this.http.post(`/api/v1/dms/documents/${documentId}/versions`, payload);
      
      console.log('[DMSDocumentService] New version created successfully:', response.data);
      
      return {
        document_id: documentId,
        version_id: response.data.version_id,
        version_number: response.data.version_number
      };

    } catch (error) {
      console.error('[DMSDocumentService] Version creation failed:', error);
      throw new Error(`Failed to create version: ${this.extractErrorMessage(error)}`);
    }
  }

  /**
   * Save working copy (auto-save functionality)
   */
  async saveWorkingCopy(documentId: string, content: string): Promise<SaveResult> {
    try {
      console.log('[DMSDocumentService] Saving working copy for document:', documentId);

      // Convert text to AST for backend storage
      const astResult = this.astConverter.convertTextToAST(content);
      
      if (!astResult.success || !astResult.ast) {
        return {
          success: false,
          message: 'AST conversion failed',
          errors: astResult.errors
        };
      }

      // Save working copy
      await this.http.post(`/api/v1/dms/documents/${documentId}/save-working-copy`, {
        content
      });

      // Note: Working copies don't need AST data in the versions table
      // AST is only saved for actual document versions, not working copies

      console.log('[DMSDocumentService] Working copy saved successfully');
      
      return {
        success: true,
        message: 'Working copy saved successfully',
        timestamp: new Date()
      };

    } catch (error) {
      console.error('[DMSDocumentService] Working copy save failed:', error);
      return {
        success: false,
        message: `Failed to save working copy: ${this.extractErrorMessage(error)}`,
        errors: [this.extractErrorMessage(error)]
      };
    }
  }

  /**
   * Save AST data for version (converted from text)
   */
  async saveVersionAST(documentId: string, versionId: string, ast: BackendAST): Promise<void> {
    try {
      await this.http.post(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
        ast
      });
      
      console.log('[DMSDocumentService] AST saved for version:', versionId);
    } catch (error) {
      console.error('[DMSDocumentService] AST save failed:', error);
      // Don't throw - AST save failure shouldn't break document save
    }
  }

  /**
   * ONLINE MODE: Update document content directly (bypass working copy)
   */
  async updateDocumentContent(documentId: string, content: string, metadata?: any): Promise<SaveResult> {
    const startTime = performance.now();
    DocumentLogger.saveStarted(documentId, 'direct');
    
    try {
      console.log('[DMSDocumentService] Updating document content directly (online mode)');

      // Get the version ID from metadata or fetch the latest version
      let versionId = metadata?.versionId;
      if (!versionId) {
        // Fetch document to get the latest version ID
        const docResponse = await this.http.get(`/api/v1/dms/documents/${documentId}`);
        versionId = docResponse.data.documents?.[0]?.latest_version_id;
        
        if (!versionId) {
          return {
            success: false,
            message: 'Version ID not available for AST save',
            errors: ['Could not determine version ID for document']
          };
        }
      }

      // Convert text to AST for backend storage
      const astResult = this.astConverter.convertTextToAST(content);
      
      if (!astResult.success || !astResult.ast) {
        return {
          success: false,
          message: 'AST conversion failed',
          errors: astResult.errors
        };
      }

      // Update the current version AST directly using the correct AST endpoint
      await this.http.post(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
        ast: astResult.ast,
        ast_version: '1.0'
      });

      console.log('[DMSDocumentService] Document content updated successfully (direct save)');
      
      const saveTimeMs = performance.now() - startTime;
      DocumentLogger.saveCompleted(documentId, {
        saveType: 'direct',
        contentLength: content.length,
        saveTimeMs,
        versionId,
      });
      
      return {
        success: true,
        message: 'Document content updated successfully',
        timestamp: new Date()
      };

    } catch (error) {
      console.error('[DMSDocumentService] Direct content update failed:', error);
      DocumentLogger.saveFailed(documentId, 'direct', this.extractErrorMessage(error));
      return {
        success: false,
        message: `Failed to update document content: ${this.extractErrorMessage(error)}`,
        errors: [this.extractErrorMessage(error)]
      };
    }
  }

  /**
   * Clear working copy from server
   */
  async clearWorkingCopy(documentId: string): Promise<SaveResult> {
    try {
      console.log('[DMSDocumentService] Clearing working copy for document:', documentId);

      await this.http.delete(`/api/v1/dms/documents/${documentId}/working-copy`);

      console.log('[DMSDocumentService] Working copy cleared successfully');
      
      return {
        success: true,
        message: 'Working copy cleared successfully',
        timestamp: new Date()
      };

    } catch (error) {
      console.error('[DMSDocumentService] Working copy clear failed:', error);
      return {
        success: false,
        message: `Failed to clear working copy: ${this.extractErrorMessage(error)}`,
        errors: [this.extractErrorMessage(error)]
      };
    }
  }

  /**
   * Load document and convert AST back to text for ClientFirst
   */
  async loadDocumentForClientFirst(documentId: string): Promise<DocumentLoadResult> {
    const startTime = performance.now();
    DocumentLogger.loadStarted(documentId);
    
    try {
      console.log('[DMSDocumentService] Loading document for ClientFirst:', documentId);

      // Get document with all versions
      const response = await this.http.get(`/api/v1/dms/documents/${documentId}?versions=all`);
      
      // Handle API response structure: { "document": { ... } }
      const document = response.data.document || response.data;

      if (!document) {
        throw new Error('Document not found');
      }
      
      console.log('[DMSDocumentService] API response structure:', {
        hasDocumentWrapper: !!response.data.document,
        documentKeys: Object.keys(document),
        versionsCount: document.versions?.length || 0
      });

      // Get latest version
      const latestVersion = document.versions && document.versions.length > 0 
        ? document.versions[0] 
        : null;

      let textContent = '';
      
      if (latestVersion) {
        if (latestVersion.ast_data) {
          // Convert AST back to text for ClientFirst
          console.log('[DMSDocumentService] Converting AST to text...');
          
          const ast = typeof latestVersion.ast_data === 'string' 
            ? JSON.parse(latestVersion.ast_data) 
            : latestVersion.ast_data;
          
          console.log('[DMSDocumentService] AST structure:', {
            hasBlocks: !!ast.blocks,
            blockCount: ast.blocks?.length || 0,
            hasMetadata: !!ast.metadata
          });
          
          const textResult = this.astConverter.convertASTToText(ast);
          if (textResult.success && textResult.text !== undefined) {
            textContent = textResult.text;
            console.log('[DMSDocumentService] AST conversion successful, text length:', textContent.length);
          } else {
            console.warn('[DMSDocumentService] AST to text conversion failed:', textResult.errors);
            
            // Manual fallback extraction
            if (ast.blocks && ast.blocks.length > 0) {
              textContent = ast.blocks.map((block: any) => {
                if (block.children && block.children.length > 0) {
                  return block.children.map((child: any) => child.text || '').join('');
                }
                return '';
              }).join('\n');
              
              console.log('[DMSDocumentService] Manual extraction fallback, text length:', textContent.length);
            } else {
              textContent = latestVersion.content || '';
            }
          }
        } else {
          // Fallback to raw content if no AST
          textContent = latestVersion.content || '';
          console.log('[DMSDocumentService] No AST data, using raw content');
        }
      }

      // Check for working copy
      const workingCopy = document.working_copy_content || undefined;

      console.log('[DMSDocumentService] Document loaded successfully:', {
        documentId,
        title: document.title,
        textLength: textContent.length,
        textPreview: textContent.substring(0, 50) + '...',
        hasWorkingCopy: !!workingCopy,
        versionsCount: document.versions?.length || 0
      });
      
      // 🚨 CRITICAL DEBUG: Log if text is empty
      if (textContent.length === 0) {
        console.error('🚨 [DMSDocumentService] CRITICAL: Text content is empty after loading!');
        console.error('🚨 This will cause empty layout plans and blank canvas');
      } else {
        console.log('✅ [DMSDocumentService] Text content loaded successfully');
      }

      // Extract page setup metadata from AST if available
      let pageSetupMetadata = null;
      if (latestVersion?.ast_data) {
        try {
          const ast = typeof latestVersion.ast_data === 'string' 
            ? JSON.parse(latestVersion.ast_data) 
            : latestVersion.ast_data;
          
          if (ast.metadata?.pageSetup) {
            pageSetupMetadata = ast.metadata.pageSetup;
            console.log('[DMSDocumentService] Found page setup metadata in AST:', pageSetupMetadata);
          }
        } catch (error) {
          console.warn('[DMSDocumentService] Failed to parse AST metadata:', error);
        }
      }
      
      // Fallback: check version metadata if no AST metadata
      if (!pageSetupMetadata && latestVersion?.metadata) {
        try {
          const metadata = typeof latestVersion.metadata === 'string' 
            ? JSON.parse(latestVersion.metadata) 
            : latestVersion.metadata;
          
          if (metadata.pageSetup) {
            pageSetupMetadata = metadata.pageSetup;
            console.log('[DMSDocumentService] Found page setup metadata in version metadata:', pageSetupMetadata);
          }
        } catch (error) {
          console.warn('[DMSDocumentService] Failed to parse version metadata:', error);
        }
      }

      // 🚨 CRITICAL DEBUG: Log final return values
      console.log('🚨 [DMS_DEBUG] DMSDocumentService returning:', {
        hasDocument: !!document,
        textLength: textContent.length,
        hasLatestVersion: !!latestVersion,
        hasWorkingCopy: !!workingCopy,
        hasPageSetupMetadata: !!pageSetupMetadata,
        pageSetupMetadataType: typeof pageSetupMetadata,
        pageSetupMetadataValue: pageSetupMetadata
      });

      const loadTimeMs = performance.now() - startTime;
      DocumentLogger.loadCompleted(documentId, {
        title: document.title,
        contentLength: textContent.length,
        versionsCount: document.versions?.length || 0,
        hasWorkingCopy: !!workingCopy,
        loadTimeMs,
      });

      return {
        document,
        textContent,
        latestVersion,
        workingCopy,
        pageSetupMetadata
      };

    } catch (error) {
      console.error('[DMSDocumentService] Document loading failed:', error);
      DocumentLogger.loadFailed(documentId, this.extractErrorMessage(error));
      throw new Error(`Failed to load document: ${this.extractErrorMessage(error)}`);
    }
  }

  /**
   * Get document with all versions
   */
  async getDocumentWithVersions(documentId: string): Promise<any> {
    try {
      const response = await this.http.get(`/api/v1/dms/documents/${documentId}?versions=all`);
      return response.data;
    } catch (error) {
      console.error('[DMSDocumentService] Failed to get document versions:', error);
      throw new Error(`Failed to get document versions: ${this.extractErrorMessage(error)}`);
    }
  }

  /**
   * Save Lexical editor content (for compatibility)
   */
  async saveLexicalContent(documentId: string, lexicalState: any): Promise<void> {
    try {
      await this.http.post(`/api/v1/dms/documents/${documentId}/lexical-content`, {
        lexicalState
      });
      
      console.log('[DMSDocumentService] Lexical content saved');
    } catch (error) {
      console.error('[DMSDocumentService] Lexical content save failed:', error);
      throw new Error(`Failed to save lexical content: ${this.extractErrorMessage(error)}`);
    }
  }

  /**
   * Get conversion statistics for monitoring
   */
  getConversionStats(text: string): any {
    const astResult = this.astConverter.convertTextToAST(text);
    if (!astResult.success || !astResult.ast) {
      return null;
    }

    return this.astConverter.getConversionStats(text, astResult.ast);
  }

  /**
   * Validate round-trip conversion for quality assurance
   */
  validateConversion(text: string, options?: ConversionOptions): any {
    return this.astConverter.validateRoundTrip(text, options);
  }

  /**
   * Update authentication token
   */
  updateToken(token: string): void {
    this.http.defaults.headers['Authorization'] = `Bearer ${token}`;
    console.log('[DMSDocumentService] Authentication token updated');
  }

  /**
   * Get service health status
   */
  async getHealthStatus(): Promise<{
    dmsConnected: boolean;
    conversionReady: boolean;
    lastCheck: Date;
  }> {
    try {
      // Test DMS connectivity with a simple request
      await this.http.get('/health');
      
      // Test conversion functionality
      const testResult = this.astConverter.convertTextToAST('Test document');
      
      return {
        dmsConnected: true,
        conversionReady: testResult.success,
        lastCheck: new Date()
      };
    } catch (error) {
      return {
        dmsConnected: false,
        conversionReady: false,
        lastCheck: new Date()
      };
    }
  }

  // Private helper methods

  private extractErrorMessage(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return 'Unknown error occurred';
  }
}